// AppPullToRefresh — единственный на приложение «потяни-чтобы-обновить».
//
// Смонтирован в оболочке (`MainLayout`) вокруг содержимого `<main>`, а не
// вокруг разметки конкретного списка. Прежняя раскладка была обратной: 23
// экрана оборачивали свой кусок собственной обёрткой, остальные ~70 — нет,
// и жест существовал пятнами. Пятна и читались как «почти нигде не
// работает»: пользователь не помнит, на каком экране обёртку поставили.
//
// Что делает жест по умолчанию: перезапрашивает АКТИВНЫЕ запросы TanStack Query
// и инвалидирует загрузчики роутера. Это надмножество того, что делали ручные
// `onRefresh={() => query.refetch()}` в обёртках, — а значит удаление обёрток
// ничего не отняло. Экран, которому нужно нетиповое обновление, регистрирует
// своё через `useRegisterPullToRefresh`.
//
// rules/cross-platform-frontend требует гейтить touch-only фичи по CAPABILITY,
// а не по ширине окна. `IS_MOBILE_PLATFORM` под это не подходит: в вебе он
// раскрывается в `window.innerWidth < 640`, снятый один раз при загрузке. То
// есть жеста не было ни на планшете, ни на телефоне, повёрнутом в альбом, ни
// после поворота экрана — а это ровно те случаи, когда «не работает» и звучит
// как «работает через раз».
//
// Здесь спрашиваем именно то, что нужно: есть ли грубый указатель. Общую
// константу не трогаем — на ней висят другие фичи (свайпы по строкам), и её
// смысл менять из-за одного жеста нельзя.

import { useHaptic } from "@studyhub/platform/hooks";
import { PullToRefresh } from "@studyhub/ui/components/pull-to-refresh";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { useDragSessionStore } from "@/stores/drag-session";
import { selectActiveHandler, usePullToRefreshStore } from "@/stores/pull-to-refresh";

/**
 * Устройство, у которого основной указатель — палец.
 *
 * Снимок на загрузку, как и остальные capability-флаги: `matchMedia` тут
 * пересчитывать нечего — тип указателя за сеанс не меняется (в отличие от
 * ширины окна, на которой прежний гейт и ломался).
 */
const supportsTouchGestures: boolean =
	IS_CAPACITOR ||
	(typeof window !== "undefined" &&
		typeof window.matchMedia === "function" &&
		window.matchMedia("(pointer: coarse)").matches);

interface AppPullToRefreshProps {
	children: ReactNode;
	/** Выключить на экранах со своей прокруткой (воркспейс курса, чат). */
	disabled?: boolean;
}

export function AppPullToRefresh({
	children,
	disabled,
}: AppPullToRefreshProps): React.ReactElement {
	const { t } = useTranslation("layout");
	const router = useRouter();
	const queryClient = useQueryClient();
	const haptic = useHaptic();
	// Уменьшенное движение — просьба не дёргать человека вообще, а не только не
	// анимировать: вибрацию гасим вместе с движением.
	const reduceMotion = usePrefersReducedMotion();
	// Во время «Хватай-и-в-коллекцию» (long-press карточки к Доку внизу) палец
	// тащит вниз при нулевой прокрутке — без этого жест читался бы как тяга.
	const isDragging = useDragSessionStore((s) => s.item !== null);
	const activeHandler = usePullToRefreshStore(selectActiveHandler);

	const handleRefresh = useCallback(async (): Promise<void> => {
		if (activeHandler) {
			await activeHandler();
			return;
		}
		// Независимые каналы — `allSettled` (rules/code-quality §29): отказ
		// одного запроса не должен отменять перезагрузку остальных.
		await Promise.allSettled([router.invalidate(), queryClient.refetchQueries({ type: "active" })]);
	}, [activeHandler, router, queryClient]);

	const handleArmedChange = useCallback(
		(armed: boolean) => {
			// Отдача ровно в кадр пересечения порога — палец должен почувствовать
			// «схватилось» ДО того, как пользователь решит отпускать. Раньше
			// вибрация приходила на отпускании, и жест читался как несработавший.
			if (armed && !reduceMotion) haptic.impact({ style: "medium" });
		},
		[haptic, reduceMotion],
	);

	if (!supportsTouchGestures) return <>{children}</>;

	return (
		<PullToRefresh
			onRefresh={handleRefresh}
			onArmedChange={handleArmedChange}
			reduceMotion={reduceMotion}
			refreshingLabel={t("pullToRefresh.refreshing")}
			// Кружок выезжает из-под шапки, поэтому его точка отсчёта — её низ
			// (шапка + стопка баннеров), а не верх безопасной зоны.
			topOffset="calc(var(--topbar-height, 0px) + var(--banners-height, 0px))"
			disabled={disabled || isDragging}
		>
			{children}
		</PullToRefresh>
	);
}
